import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { useFavorites } from '@/hooks/useFavorites';
import { useScrollReveal } from '@/hooks/useScrollReveal';

/* ── Deal of the day data ── */
const DEAL = {
  id: 5,
  name: 'NeuroPlex Ultra 500 мг',
  subtitle: 'Нейропротектор нового поколения',
  description: 'Инновационный нейропротектор на основе фосфатидилсерина и экстракта гинкго билоба. Улучшает память, концентрацию и снижает умственную усталость. Клинически доказанная эффективность.',
  price: 1490,
  oldPrice: 2980,
  discount: 50,
  color: '#ff2d78',
  image: 'https://public.readdy.ai/ai/img_res/7204ccdb5bba33238fa2353b3b642c49.jpg',
  rating: 4.9,
  reviews: 1247,
  stock: 23,
  sold: 77,
  endsAt: (() => {
    const d = new Date();
    d.setHours(23, 59, 59, 0);
    return d.toISOString();
  })(),
  features: [
    { icon: 'ri-brain-line', text: 'Улучшает память и концентрацию' },
    { icon: 'ri-battery-charge-line', text: 'Снижает умственную усталость' },
    { icon: 'ri-moon-line', text: 'Нормализует сон' },
    { icon: 'ri-leaf-line', text: 'Натуральный состав' },
    { icon: 'ri-shield-check-line', text: 'Клинически доказано' },
    { icon: 'ri-award-line', text: 'Без рецепта' },
  ],
  badge: 'АКЦИЯ ДНЯ',
  badgeColor: '#ff2d78',
};

/* ── Countdown ── */
const calcTime = (endsAt: string) => {
  const diff = Math.max(0, new Date(endsAt).getTime() - Date.now());
  return {
    h: Math.floor(diff / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
};

const pad = (n: number) => String(n).padStart(2, '0');

const BigCountdown = ({ endsAt, color }: { endsAt: string; color: string }) => {
  const [time, setTime] = useState(() => calcTime(endsAt));
  useEffect(() => {
    const t = setInterval(() => setTime(calcTime(endsAt)), 1000);
    return () => clearInterval(t);
  }, [endsAt]);

  const units = [
    { val: time.h, label: 'часов' },
    { val: time.m, label: 'минут' },
    { val: time.s, label: 'секунд' },
  ];

  return (
    <div className="flex items-center gap-3">
      {units.map((u, i) => (
        <div key={u.label} className="flex items-center gap-3">
          <div
            className="flex flex-col items-center px-4 py-3 rounded-2xl min-w-[72px]"
            style={{
              background: `${color}12`,
              border: `1px solid ${color}40`,
              boxShadow: `0 0 20px ${color}15`,
            }}
          >
            <span
              className="font-orbitron font-black text-4xl leading-none tabular-nums"
              style={{ color, textShadow: `0 0 16px ${color}` }}
            >
              {pad(u.val)}
            </span>
            <span className="font-mono text-[10px] mt-1.5 text-white/35">{u.label}</span>
          </div>
          {i < units.length - 1 && (
            <span
              className="font-orbitron font-black text-3xl mb-5"
              style={{ color, opacity: 0.4, animation: 'pulse 1s ease-in-out infinite' }}
            >
              :
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

/* ── Stock bar ── */
const StockBar = ({ sold, stock, color }: { sold: number; stock: number; color: string }) => {
  const pct = Math.round((sold / (sold + stock)) * 100);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-xs text-white/40">Уже купили сегодня</span>
        <span className="font-orbitron font-bold text-sm" style={{ color }}>
          {sold} чел.
        </span>
      </div>
      <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${color}, ${color}80)`,
            boxShadow: `0 0 10px ${color}60`,
          }}
        />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="font-mono text-[10px] text-white/25">0</span>
        <span className="font-mono text-xs animate-pulse" style={{ color: '#ff6b00' }}>
          Осталось {stock} шт.!
        </span>
        <span className="font-mono text-[10px] text-white/25">{sold + stock}</span>
      </div>
    </div>
  );
};

/* ── Main component ── */
const PromoDealOfDay = () => {
  const navigate = useNavigate();
  const { addItem, hasItem } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { ref: sectionRef, isVisible } = useScrollReveal({ threshold: 0.1 });
  const [qty, setQty] = useState(1);

  const inCart = hasItem(DEAL.id);
  const inFav = isFavorite(DEAL.id);
  const totalPrice = DEAL.price * qty;
  const saving = (DEAL.oldPrice - DEAL.price) * qty;

  const handleCart = () => {
    addItem({
      id: DEAL.id,
      name: DEAL.name,
      price: DEAL.price,
      oldPrice: DEAL.oldPrice,
      image: DEAL.image,
      color: DEAL.color,
    });
  };

  const handleFav = () => {
    toggleFavorite({
      id: DEAL.id,
      name: DEAL.name,
      price: DEAL.price,
      oldPrice: DEAL.oldPrice,
      image: DEAL.image,
      color: DEAL.color,
      discount: DEAL.discount,
      rating: DEAL.rating,
      inStock: true,
    });
  };

  return (
    <section className="px-6 lg:px-12 pb-16">
      <div className="max-w-7xl mx-auto">

        {/* Section label */}
        <div
          ref={sectionRef as React.RefObject<HTMLDivElement>}
          className="flex items-center gap-4 mb-8"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs font-bold"
              style={{ background: 'rgba(255,45,120,0.12)', border: '1px solid rgba(255,45,120,0.35)', color: '#ff2d78' }}
            >
              <i className="ri-fire-line animate-pulse" />
              АКЦИЯ ДНЯ
            </div>
            <span className="font-mono text-xs text-white/30">Обновляется каждые 24 часа</span>
          </div>
          <div className="h-px flex-1" style={{ background: 'linear-gradient(to right, rgba(255,45,120,0.3), transparent)' }} />
        </div>

        {/* Main card */}
        <div
          className="relative rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #0d0d1a 0%, rgba(255,45,120,0.06) 100%)',
            border: '1px solid rgba(255,45,120,0.25)',
            boxShadow: '0 0 60px rgba(255,45,120,0.08)',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s, transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s',
          }}
        >
          {/* Top gradient line */}
          <div
            className="h-0.5 w-full"
            style={{ background: 'linear-gradient(90deg, #ff2d78, #ff6b00, #f0ff00, transparent)' }}
          />

          {/* BG glow */}
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10 pointer-events-none" style={{ background: '#ff2d78', filter: 'blur(100px)', transform: 'translate(30%, -30%)' }} />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-8 pointer-events-none" style={{ background: '#f0ff00', filter: 'blur(80px)', transform: 'translate(-30%, 30%)' }} />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-0">

            {/* Left: image + countdown */}
            <div
              className="flex flex-col items-center justify-center p-10 relative"
              style={{ borderRight: '1px solid rgba(255,45,120,0.12)' }}
            >
              {/* Badges */}
              <div className="absolute top-6 left-6 flex flex-col gap-2">
                <span
                  className="px-3 py-1.5 rounded-full font-orbitron font-black text-sm"
                  style={{ background: DEAL.badgeColor, color: '#050508', boxShadow: `0 0 14px ${DEAL.badgeColor}80` }}
                >
                  {DEAL.badge}
                </span>
                <span
                  className="px-3 py-1.5 rounded-full font-orbitron font-black text-lg"
                  style={{ background: 'rgba(255,45,120,0.15)', border: '2px solid #ff2d78', color: '#ff2d78', boxShadow: '0 0 16px rgba(255,45,120,0.4)' }}
                >
                  -{DEAL.discount}%
                </span>
              </div>

              {/* Product image */}
              <div className="relative mb-8">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{ background: DEAL.color, filter: 'blur(60px)', opacity: 0.2 }}
                />
                <img
                  src={DEAL.image}
                  alt={DEAL.name}
                  className="w-64 h-64 object-contain relative z-10"
                  style={{ filter: `drop-shadow(0 20px 40px ${DEAL.color}60)` }}
                />
              </div>

              {/* Countdown */}
              <div className="text-center">
                <p className="font-mono text-xs text-white/35 mb-3 flex items-center justify-center gap-2">
                  <i className="ri-alarm-line" style={{ color: DEAL.color }} />
                  Акция заканчивается через:
                </p>
                <BigCountdown endsAt={DEAL.endsAt} color={DEAL.color} />
              </div>
            </div>

            {/* Right: info + purchase */}
            <div className="p-8 lg:p-10 flex flex-col justify-between gap-6">

              {/* Product info */}
              <div>
                <p className="font-mono text-xs text-white/35 mb-2">{DEAL.subtitle}</p>
                <h2
                  className="font-orbitron font-black text-2xl md:text-3xl text-white leading-tight mb-3"
                  style={{ textShadow: `0 0 20px ${DEAL.color}30` }}
                >
                  {DEAL.name}
                </h2>
                <p className="font-mono text-sm text-white/50 leading-relaxed mb-5">
                  {DEAL.description}
                </p>

                {/* Features grid */}
                <div className="grid grid-cols-2 gap-2 mb-5">
                  {DEAL.features.map((f, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                    >
                      <div className="w-4 h-4 flex items-center justify-center shrink-0">
                        <i className={f.icon} style={{ color: DEAL.color, fontSize: '13px' }} />
                      </div>
                      <span className="font-mono text-[11px] text-white/55">{f.text}</span>
                    </div>
                  ))}
                </div>

                {/* Rating */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(s => (
                      <i key={s} className={`text-sm ${s <= Math.round(DEAL.rating) ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
                    ))}
                  </div>
                  <span className="font-orbitron font-bold text-base" style={{ color: '#f0ff00' }}>{DEAL.rating}</span>
                  <span className="font-mono text-xs text-white/30">({DEAL.reviews.toLocaleString()} отзывов)</span>
                </div>

                {/* Stock bar */}
                <StockBar sold={DEAL.sold} stock={DEAL.stock} color={DEAL.color} />
              </div>

              {/* Purchase block */}
              <div className="space-y-4">
                {/* Price */}
                <div
                  className="flex items-end gap-4 p-4 rounded-2xl"
                  style={{ background: `${DEAL.color}08`, border: `1px solid ${DEAL.color}20` }}
                >
                  <div>
                    <span
                      className="font-orbitron font-black text-4xl"
                      style={{ color: DEAL.color, textShadow: `0 0 20px ${DEAL.color}70` }}
                    >
                      {(DEAL.price * qty).toLocaleString()} ₽
                    </span>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="font-mono text-sm text-white/25 line-through">{(DEAL.oldPrice * qty).toLocaleString()} ₽</span>
                      <span
                        className="font-mono text-xs px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(57,255,20,0.1)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.25)' }}
                      >
                        Экономия {saving.toLocaleString()} ₽
                      </span>
                    </div>
                  </div>
                </div>

                {/* Qty + cart */}
                <div className="flex items-center gap-3">
                  {/* Qty */}
                  <div
                    className="flex items-center rounded-xl overflow-hidden shrink-0"
                    style={{ border: `1px solid ${DEAL.color}30` }}
                  >
                    <button
                      onClick={() => setQty(q => Math.max(1, q - 1))}
                      className="w-10 h-11 flex items-center justify-center cursor-pointer transition-colors text-white/50 hover:text-white"
                      style={{ background: 'rgba(255,255,255,0.03)' }}
                    >
                      <i className="ri-subtract-line" />
                    </button>
                    <div
                      className="w-12 h-11 flex items-center justify-center font-orbitron font-bold text-base"
                      style={{ color: DEAL.color, borderLeft: `1px solid ${DEAL.color}20`, borderRight: `1px solid ${DEAL.color}20` }}
                    >
                      {qty}
                    </div>
                    <button
                      onClick={() => setQty(q => Math.min(DEAL.stock, q + 1))}
                      className="w-10 h-11 flex items-center justify-center cursor-pointer transition-colors text-white/50 hover:text-white"
                      style={{ background: 'rgba(255,255,255,0.03)' }}
                    >
                      <i className="ri-add-line" />
                    </button>
                  </div>

                  {/* Add to cart */}
                  <button
                    onClick={handleCart}
                    className="whitespace-nowrap flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-orbitron font-bold text-sm cursor-pointer transition-all duration-300"
                    style={
                      inCart
                        ? { background: '#39ff14', color: '#050508', boxShadow: '0 0 20px rgba(57,255,20,0.5)' }
                        : { background: DEAL.color, color: '#050508', boxShadow: `0 0 20px ${DEAL.color}60` }
                    }
                  >
                    <i className={inCart ? 'ri-check-double-line' : 'ri-shopping-bag-line'} />
                    {inCart ? 'В КОРЗИНЕ' : 'В КОРЗИНУ'}
                  </button>

                  {/* Favorite */}
                  <button
                    onClick={handleFav}
                    className="w-12 h-12 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-300 shrink-0"
                    style={{
                      border: inFav ? '2px solid #ff2d78' : '2px solid rgba(255,255,255,0.1)',
                      background: inFav ? 'rgba(255,45,120,0.15)' : 'transparent',
                      boxShadow: inFav ? '0 0 12px rgba(255,45,120,0.3)' : 'none',
                    }}
                  >
                    <i
                      className={inFav ? 'ri-heart-fill' : 'ri-heart-line'}
                      style={{ color: inFav ? '#ff2d78' : 'rgba(255,255,255,0.35)', fontSize: '18px' }}
                    />
                  </button>
                </div>

                {/* Go to product */}
                <button
                  onClick={() => navigate(`/product/${DEAL.id}`)}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-3 rounded-xl font-mono text-sm cursor-pointer transition-all duration-200"
                  style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${DEAL.color}40`; (e.currentTarget as HTMLElement).style.color = DEAL.color; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.4)'; }}
                >
                  <i className="ri-external-link-line" />
                  Открыть страницу товара
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PromoDealOfDay;
